import React, { useState, useEffect } from 'react';
import DynamicReviewForm from './DynamicReviewForm';
import './ReviewFormWithSave.css';
import useLocale from '../hooks/useLocale';

const ReviewFormWithSave = ({ template, onSave, currentValues, currentStatus, editable }) => {
  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();
  const getMissingFields = (values) => {
    const missing = [];
    template.forEach((block) => {
      if (block?.required === false) return;

      const raw = values?.[block.id];
      const v = typeof raw === 'string' ? raw.trim() : raw;

      if (block.type === 'textarea') {
        if (!v) missing.push(block.label);
      } else if (block.type === 'select') {
        if (!v) missing.push(block.label);
      }
    });
    return missing;
  };

  // Инициализируем значения для динамических полей по id блока
  const getInitialValues = () => {
    const values = {};
    template.forEach((block) => {
      values[block.id] = currentValues?.[block.id] ?? '';
    });
    return values;
  };

  const [formValues, setFormValues] = useState(getInitialValues());
  // Статус отзыва:
  // - по умолчанию "in_progress" (не выбирается в селекте),
  // - если текущий статус финальный, отображаем его,
  // - пустая строка в state означает "статус не выбран" (оставить in_progress).
  const [reviewStatus, setReviewStatus] = useState(currentStatus ? currentStatus : 'in_progress');

  const missingFields = getMissingFields(formValues);
  const formComplete = missingFields.length === 0;
  const isFinalStatusSelected = reviewStatus !== 'in_progress';
  const canSubmit = isFinalStatusSelected && formComplete;

  useEffect(() => {
    // При изменении шаблона обновляем значения, сохраняя уже введённые данные
    setFormValues((prev) => {
      const newValues = { ...prev };
      template.forEach((block) => {
        if (!(block.id in newValues)) {
          newValues[block.id] = '';
        }
      });
      return newValues;
    });
  }, [template]);

  const handleChange = (newValues) => {
    setFormValues(newValues);
  };

  const handleSave = () => {
    if (onSave) {
      // If final status is not selected (reviewStatus === 'in_progress'),
      // pass null so backend keeps it as "in_progress".
      const normalizedStatus = reviewStatus && reviewStatus !== 'in_progress' ? reviewStatus : null;
      onSave({ ...formValues }, normalizedStatus);
    }
  };

  return (
    <div className="review-form-with-save">
      <DynamicReviewForm
        template={template}
        formValues={formValues}
        onChange={handleChange}
        editable={editable}
      />
      <div className="review-status-section">
        <label className="review-status-label">
          {lang === 'ru' ? 'Статус отзыва:' : 'Review status:'}
        </label>
        <select
          className="review-status-select"
          value={reviewStatus}
          disabled={!editable}
          onChange={(e) => setReviewStatus(e.target.value)}
        >
          {/* Show as current value, but do not offer as a selectable choice */}
          <option value="in_progress" hidden>
            {lang === 'ru' ? 'В процессе' : 'In progress'}
          </option>
          <option value="finalized" disabled hidden>
            {lang === 'ru' ? 'Завершено' : 'Finalized'}
          </option>
          <option value="approved">{lang === 'ru' ? 'Одобрено' : 'Approved'}</option>
          <option value="revision_required">
            {lang === 'ru' ? 'Требуется доработка' : 'Revision required'}
          </option>
          <option value="rejected">{lang === 'ru' ? 'Отклонено' : 'Rejected'}</option>
        </select>
      </div>
      {editable && (
        <>
          <button
            className="review-form-save-btn"
            onClick={handleSave}
            disabled={!canSubmit}
            title={
              !isFinalStatusSelected
                ? lang === 'ru'
                  ? 'Выберите итоговый статус (Одобрено / Отклонено / Требуется доработка)'
                  : 'Choose a final status (Approved / Denied / Revision required)'
                : !formComplete
                  ? lang === 'ru'
                    ? 'Заполните все обязательные поля (*) перед отправкой'
                    : 'Fill in all required fields (*) before submitting'
                  : undefined
            }
          >
            {lang === 'ru' ? 'Сохранить отзыв' : 'Save review'}
          </button>

          {isFinalStatusSelected && !formComplete && (
            <div className="review-form-validation">
              <div className="review-form-validation-title">
                {lang === 'ru'
                  ? 'Перед отправкой заполните все обязательные поля (*):'
                  : 'Fill in all required fields (*) before submitting:'}
              </div>
              <ul className="review-form-validation-list">
                {missingFields.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewFormWithSave;
